<script setup>
/**
 * Modal "Registrar abono" (Victor 2026-06-07 · Fase 2). Suma un pago a la reserva
 * y baja el pendiente. Guarda quién lo registró (el agente conectado).
 */
import { ref, computed } from 'vue';
import ReservationsAPI from 'dashboard/api/reservations';

const props = defineProps({
  reservationId: { type: [Number, String], required: true },
  total: { type: [Number, String], default: 0 },
  pending: { type: [Number, String], default: 0 },
});
const emit = defineEmits(['close', 'saved']);

const PAYMENT_TYPES = ['Efectivo', 'Transferencia', 'Tarjeta'];

const amount = ref('');
const paymentType = ref('Efectivo');
const saving = ref(false);
const error = ref(false);

const fmt = n =>
  `RD$ ${(Number(n) || 0).toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;

const newPending = computed(() =>
  Math.max(0, (Number(props.pending) || 0) - (Number(amount.value) || 0))
);

const canSave = computed(() => Number(amount.value) > 0 && !saving.value);

const save = async () => {
  if (!canSave.value) return;
  saving.value = true;
  error.value = false;
  try {
    await ReservationsAPI.addPayment(props.reservationId, {
      amount: Number(amount.value),
      payment_type: paymentType.value,
    });
    emit('saved');
    emit('close');
  } catch (e) {
    error.value = true;
  } finally {
    saving.value = false;
  }
};
</script>

<template>
  <teleport to="body">
    <div class="ab-overlay" @click.self="emit('close')">
      <div class="ab-modal">
        <div class="ab-head">
          <span class="ab-ic">💵</span>
          <div class="ab-title">Registrar abono</div>
          <button class="ab-x" @click="emit('close')">✕</button>
        </div>
        <div class="ab-body">
          <div class="ab-summary">
            <div><span>Pendiente actual</span><b class="red">{{ fmt(pending) }}</b></div>
            <div><span>Nuevo pendiente</span><b class="green">{{ fmt(newPending) }}</b></div>
          </div>
          <label class="ab-l">Monto del abono *</label>
          <input v-model="amount" class="ab-inp" type="number" min="0" placeholder="0" autofocus />
          <label class="ab-l ab-mt">Tipo de pago</label>
          <div class="ab-seg">
            <button
              v-for="t in PAYMENT_TYPES"
              :key="t"
              class="ab-o"
              :class="{ on: paymentType === t }"
              @click="paymentType = t"
            >{{ t }}</button>
          </div>
          <p v-if="error" class="ab-err">No se pudo registrar, intenta de nuevo.</p>
        </div>
        <div class="ab-foot">
          <button class="ab-btn ghost" @click="emit('close')">Cancelar</button>
          <button class="ab-btn gold" :disabled="!canSave" @click="save">💵 Registrar abono</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style lang="scss" scoped>
.ab-overlay { position: fixed; inset: 0; z-index: 10001; background: rgba(0, 0, 0, 0.6); display: grid; place-items: center; padding: 20px; }
.ab-modal { width: 420px; max-width: 100%; background: linear-gradient(180deg, #141416, #0d0d0f); border: 1px solid rgba(201, 163, 90, 0.5); border-radius: 16px; overflow: hidden; box-shadow: 0 30px 80px rgba(0, 0, 0, 0.6); }
.ab-head { display: flex; align-items: center; gap: 10px; padding: 15px 18px; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.ab-ic { width: 32px; height: 32px; border-radius: 9px; background: rgba(201, 163, 90, 0.12); border: 1px solid rgba(201, 163, 90, 0.5); display: grid; place-items: center; font-size: 15px; }
.ab-title { font-size: 15px; font-weight: 800; color: #fff; }
.ab-x { margin-left: auto; width: 28px; height: 28px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); background: none; color: #8b8b91; cursor: pointer; }
.ab-body { padding: 16px 18px; }
.ab-summary { display: flex; gap: 10px; margin-bottom: 14px; }
.ab-summary > div { flex: 1; background: #0e0e10; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 10px; padding: 9px 11px; }
.ab-summary span { display: block; font-size: 10px; color: #8b8b91; margin-bottom: 3px; }
.ab-summary b { font-size: 15px; }
.ab-summary b.red { color: #cc6b6b; }
.ab-summary b.green { color: #5fae7e; }
.ab-l { display: block; font-size: 11px; color: #8b8b91; font-weight: 600; margin-bottom: 5px; }
.ab-mt { margin-top: 13px; }
.ab-inp { width: 100%; background: #0e0e10; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 9px; padding: 10px 12px; font-size: 15px; color: #fff; outline: none; }
.ab-inp:focus { border-color: rgba(201, 163, 90, 0.6); }
.ab-seg { display: flex; gap: 5px; }
.ab-o { flex: 1; font-size: 12px; font-weight: 700; padding: 9px 4px; border-radius: 8px; border: 1px solid rgba(255, 255, 255, 0.08); color: #8b8b91; background: none; cursor: pointer; }
.ab-o.on { color: #5fae7e; background: rgba(95, 174, 126, 0.14); border-color: rgba(95, 174, 126, 0.4); }
.ab-err { font-size: 12px; color: #cc6b6b; margin-top: 10px; }
.ab-foot { padding: 13px 18px; display: flex; gap: 10px; border-top: 1px solid rgba(255, 255, 255, 0.08); }
.ab-btn { flex: 1; text-align: center; font-size: 13px; font-weight: 800; border-radius: 10px; padding: 11px; cursor: pointer; border: none; }
.ab-btn.gold { background: #c9a35a; color: #1a1206; flex: 2; }
.ab-btn.gold:disabled { opacity: 0.4; cursor: default; }
.ab-btn.ghost { background: none; border: 1px solid rgba(255, 255, 255, 0.1); color: #8b8b91; }
</style>
